import { ColorPalette, createLightTheme } from '@ui-components/ui-library';

export const UTColorPalette: Partial<ColorPalette> = {
  primary: {
    50: '#e6f2f4', // Very light teal
    100: '#b3dde3', // Light teal
    200: '#80c7d1', // Medium light teal
    300: '#588890', // ocean-300 (light from your palette)
    400: '#3e7179', // Medium teal
    500: '#054e5a', // primaryteal (main)
    600: '#044549', // Slightly darker
    700: '#043c42', // Dark teal
    800: '#032b32', // ocean-800 (dark from your palette)
    900: '#021f24', // Very dark teal
  },
  tertiary: {
    50: '#f8f9fa', // Very light neutral
    100: '#e7eaee', // neutral-100 from your palette
    200: '#dfe4e7', // text-colorgrey-10 from your palette
    300: '#c1ccd3', // Light grey
    400: '#9caab3', // Medium light grey
    500: '#7a8b94', // Medium grey
    600: '#4d5d64', // neutral-600 from your palette
    700: '#3a474e', // Dark grey
    800: '#23333b', // neutral-800 from your palette
    900: '#17242c', // neutral-900 from your palette
  },
  success: {
    50: '#e8f5e8', // Very light green
    100: '#c3e6c3', // Light green
    200: '#9dd69d', // Medium light green
    300: '#77c677', // Light green
    400: '#5bb75b', // Medium green
    500: '#4caf50', // Main success green
    600: '#43a047', // Slightly darker green
    700: '#388e3c', // Dark green
    800: '#2e7d32', // Darker green
    900: '#1b5e20', // Very dark green
  },
  warning: {
    50: '#fff8e1', // Very light amber
    100: '#ffecb3', // Light amber
    200: '#ffe082', // Medium light amber
    300: '#ffd54f', // Light amber
    400: '#ffca28', // Medium amber
    500: '#ffc107', // Main warning amber
    600: '#ffb300', // Slightly darker amber
    700: '#ffa000', // Dark amber
    800: '#ff8f00', // Darker amber
    900: '#ff6f00', // Very dark amber
  },
  error: {
    50: '#ffebee', // Very light red
    100: '#ffcdd2', // Light red
    200: '#ef9a9a', // Medium light red
    300: '#e57373', // Light red
    400: '#ef5350', // Medium red
    500: '#f44336', // Main error red
    600: '#e53935', // Slightly darker red
    700: '#d32f2f', // Dark red
    800: '#c62828', // Darker red
    900: '#b71c1c', // Very dark red
  },
  info: {
    50: '#e3f2fd', // Very light blue
    100: '#bbdefb', // Light blue
    200: '#90caf9', // Medium light blue
    300: '#64b5f6', // Light blue
    400: '#42a5f5', // Medium blue
    500: '#2196f3', // Main info blue
    600: '#1e88e5', // Slightly darker blue
    700: '#1976d2', // Dark blue
    800: '#1565c0', // Darker blue
    900: '#0d47a1', // Very dark blue
  },
};

const customTheme: any = {
  typography: {
    fontFamily: ['Inter', 'Noto Sans', 'Source Sans Pro', 'Segoe UI-Regular', 'sans-serif'].join(
      ','
    ),
    h3: {
      fontFamily: 'Inter, Helvetica',
      fontSize: '28px',
      fontWeight: 600,
      lineHeight: '36px',
    },
    h4: {
      fontFamily: 'Inter, Helvetica',
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: '32px',
    },
    button: {
      fontFamily: 'Inter, Helvetica',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '22px',
      textTransform: 'none',
    },
    body1: {
      fontFamily: 'Inter, Helvetica',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '22px',
    },
    body2: {
      fontFamily: 'Inter, Helvetica',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '22px',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Inter, Helvetica, sans-serif',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body1: 'p',
          body2: 'span',
        },
      },
      styleOverrides: {
        root: {
          fontFamily: 'Inter, Helvetica, sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          padding: '8px 12px',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#032b32',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#054e5a',
          color: '#ffffff',
          padding: '0px !important',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '0.5px solid #8e9296',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '72px',
          backgroundColor: '#ffffff',
          '@media (min-width:600px)': {
            minHeight: '72px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            width: '100%',
            minHeight: '36px',
            borderRadius: '6px',
          },
          '& .MuiInputLabel-root': {
            fontSize: '15px',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '& svg': {
            color: 'none',
          },
          '& .MuiListItem-root:hover': {
            backgroundColor: 'none',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 38,
          height: 22,
          borderRadius: 22,
          background:'#f8f8f8',
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#054E5A',
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.1,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: '#054E5A',
              border: '6px solid #fff',
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 18,
              height: 18,
            },
            '& .MuiSwitch-track': {
              backgroundColor: '#9FA7AB',
              opacity: 0.1,
            },
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1024,
      xl: 1440,
    },
  },
};
const theme = createLightTheme(UTColorPalette, customTheme);
export default theme;
